import React, { FocusEvent } from "react";
import { Row, Col } from "react-bootstrap";
import { ActionHandlerFunction, AdminUser } from "@preveil-api";
import { account_types, AdminUIActionTypes, CheckboxStates, DriveUIActionTypes, getAccountTooltip, OrgUserStatus, pending_account_updates_types } from "src/common";
import { Checkbox, Icon, InfoPopover } from "src/components";

type AllProps = {
  user: AdminUser;
  selected: boolean;
  handleAction: ActionHandlerFunction;
};

function UserListRowComponent(props: AllProps) {
  const { user, handleAction, selected } = props;
  const { pending_recovery_group_id } = user;
  const total_pending_requests: number = user.updates_pending && user.updates_pending >= 1 ? user.updates_pending : 0;

  // Description: show side nav with the user details
  function handleRowClick() {
    handleAction({ actionType: AdminUIActionTypes.ShowSideNavMenu, params: { user } });
  }

  // Description: Gets the account Recovery Group if there is no recovery group assigned then show "Not Assigned".
  function getAssignedRecoveryGroup(pending_recovery_group_id: string | null | undefined): JSX.Element | null {
    if (user.status === OrgUserStatus.active) {
      const name = !!user.recovery_group ? user.recovery_group.name : "Not Assigned";
      const is_pending = !!pending_recovery_group_id || (pending_recovery_group_id !== user.pending_recovery_group_id);
      const group_label = is_pending ? "Pending New Recovery Group" : name;
      return <span className={`${!user.recovery_group || is_pending ? "no-recovery-group" : ""}`}>{group_label}</span>;
    }
    return null;
  }

  // Description: This function will return a template/string for accounts pending requests
  // so we can use it with the popover component, it will break the pending updates/requests
  // by type along with the total count of each type (only if it is greater than 0). If there are no pending requests
  // for an specific request type it won't be included in the template.
  function createPendingUpdatesTemplate(user: AdminUser): string {
    return pending_account_updates_types
      .filter(pending_update => {
        const pending_type = user[pending_update.key as keyof AdminUser] as number;
        return pending_type >= 1;
      })
      .map(pending_update => {
        const total = user[pending_update.key as keyof AdminUser];
        const message = pending_update.message;
        return `${total} ${message}`;
      }).join("<br>");
  }

  function getAccountPendingUpdates(): { total_pending: number, message: string } {
    const template = {
      total_pending: 0,
      message: ""
    };
    if (total_pending_requests >= 1) {
      template.total_pending = total_pending_requests;
      template.message = createPendingUpdatesTemplate(user);
    } else if (user.status === OrgUserStatus.invited || user.status === OrgUserStatus.pending) {
      template.message = user.status === OrgUserStatus.invited ? "User invitation pending." : "External user invitation pending.";
      template.total_pending = 1;
    } else if (user.pending_account_type === account_types.express) {
      template.message = "User conversion pending";
      template.total_pending = 1;
    }
    return template;
  }

  return <Row onClick={handleRowClick}>
    <Col md="1">
      <Checkbox
        className="sr-only"
        onChange={(e: FocusEvent<HTMLInputElement>) => { handleAction({ actionType: DriveUIActionTypes.Select, params: { selected: e.target.checked, user } }); }}
        selected={selected ? CheckboxStates.checked : CheckboxStates.empty}
        value="all" label="Select All" />
    </Col>
    <Col md="3" className="col">
      <Icon
        tooltip={getAccountTooltip(user.account_type, user.pending_account_type)}
        className={`account-type-icon ${user.account_type === account_types.full ? user.pending_account_type === account_types.express ? "icon-user inactive" : "icon-user-plus" : user.account_type === account_types.express ? "icon-user" : "icon-user-plus inactive"}`} />
      {user.display_name}
    </Col>
    <Col md="2">
      {user.user_id}
    </Col>
    <Col md="2">
      {user.department}
    </Col>
    {
      user.status === OrgUserStatus.active && <Col>
        <span className="text-capitalize">{user.role}</span>
      </Col>
    }
    {
      getAssignedRecoveryGroup(pending_recovery_group_id) && <Col md="3">
        {getAssignedRecoveryGroup(pending_recovery_group_id)}
      </Col>
    }
    <Col md="1" className={`due-column ${getAccountPendingUpdates().total_pending === 0 ? "hide-mobile" : ""}`} onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}>
      {getAccountPendingUpdates().total_pending >= 1 && (
        <InfoPopover
          placement="bottom-end"
          message={getAccountPendingUpdates().message}
          button_text={getAccountPendingUpdates().total_pending.toString()}
        />
      )}
    </Col>
  </Row>;
}

export default React.memo(UserListRowComponent); 
